import { supabase } from "../lib/supabase";
import {
  getCurrentCurrency,
  getPriceForCurrentPlatform,
} from "../utils/platform";

export const bundleService = {
  /**
   * Get all active bundles
   */
  async getAllBundles() {
    try {
      const { data, error } = await supabase
        .from("course_bundles")
        .select(
          `
          *,
          bundle_courses (
            sort_order,
            course:courses (
              id,
              title,
              duration_hours,
              price_inr,
              price_usd,
              price_cad,
              thumbnail_url,
              course_types,
              difficulty_level
            )
          )
        `,
        )
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Flatten course data and compute totals
      return (data || []).map((bundle) => {
        const courses = (bundle.bundle_courses || [])
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((bc) => bc.course)
          .filter(Boolean);

        const totalDuration = courses.reduce(
          (sum, c) => sum + (c.duration_hours || 0),
          0,
        );
        const totalPriceInr = courses.reduce(
          (sum, c) => sum + (parseFloat(c.price_inr) || 0),
          0,
        );
        const totalPriceUsd = courses.reduce(
          (sum, c) => sum + (parseFloat(c.price_usd) || 0),
          0,
        );
        const totalPriceCad = courses.reduce(
          (sum, c) => sum + (parseFloat(c.price_cad) || 0),
          0,
        );

        delete bundle.bundle_courses;

        // Compute savings percentage based on current platform currency
        const bundlePrice = getPriceForCurrentPlatform({
          price_inr: bundle.bundle_price_inr,
          price_usd: bundle.bundle_price_usd,
          price_cad: bundle.bundle_price_cad,
        });
        const originalPrice = getPriceForCurrentPlatform({
          price_inr: totalPriceInr,
          price_usd: totalPriceUsd,
          price_cad: totalPriceCad,
        });
        const savingsPercentage =
          originalPrice > 0 && originalPrice > bundlePrice
            ? Math.round(((originalPrice - bundlePrice) / originalPrice) * 100)
            : 0;

        return {
          ...bundle,
          courses,
          course_count: courses.length,
          total_duration_hours: totalDuration,
          original_price_inr: totalPriceInr,
          original_price_usd: totalPriceUsd,
          original_price_cad: totalPriceCad,
          savings_percentage: savingsPercentage,
        };
      });
    } catch (error) {
      console.error("Error fetching bundles:", error);
      throw error;
    }
  },

  /**
   * Get bundle by ID with courses
   */
  async getBundleById(bundleId) {
    try {
      const { data, error } = await supabase.rpc("get_bundle_with_courses", {
        p_bundle_id: bundleId,
      });

      if (error) throw error;
      // RPC returns RETURNS JSON (single object, not array), so data IS the object directly.
      // Shape: { bundle: {...}, courses: [...] } — flatten for callers
      if (!data) return null;
      const raw = Array.isArray(data) ? data[0] : data;
      if (!raw) return null;
      // Handle both possible shapes: nested { bundle, courses } or already flat
      if (raw.bundle) {
        return { ...raw.bundle, courses: raw.courses || [] };
      }
      return raw;
    } catch (error) {
      console.error("Error fetching bundle:", error);
      throw error;
    }
  },

  /**
   * Get bundle savings
   */
  async getBundleSavings(bundleId) {
    try {
      const { data, error } = await supabase.rpc("calculate_bundle_savings", {
        p_bundle_id: bundleId,
        p_currency: getCurrentCurrency(),
      });

      if (error) throw error;
      return data || 0;
    } catch (error) {
      console.error("Error calculating savings:", error);
      return 0;
    }
  },
};
